class WlsSearchModal extends HTMLElement {
    constructor() {
      super();
  
      this.state = {
        isOpen: false,
        query: '',
        hasResults: false,
      };
  
      this.config = {
        triggerSelector: '.site-header__icon--search',
        activeClass: 'is-active',
        resultsClass: 'has-results',
        minChars: 2,
        debounceMs: 200,
        limit: 6,
      };
  
      this._abortController = null;
      this._debounceTimer = null;
  
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleInput = this.handleInput.bind(this);
    }
  
    connectedCallback() {
      // Elements
      this.inner = this.querySelector('.js-wls-search-inner');
      this.closeBtn = this.querySelector('.js-wls-search-close');
      this.input = this.querySelector('.wls-search__input');
  
      this.dropdown = this.querySelector('.wls-search__dropdown');
      this.resultsWrap = this.querySelector('.wls-search__results');
  
      // Create empty state container if not present (since you commented it out)
      this.emptyWrap = this.querySelector('.wls-search__empty') || this._createEmpty();
  
      // Targets for rendering (create if missing)
      this.suggestionsList = this.querySelector('[data-search-suggestions]') || null;
      this.collectionsList = this.querySelector('[data-search-collections]') || null;
      this.productsGrid = this.querySelector('[data-search-products]') || null;
      this.viewAllLink = this.querySelector('[data-search-viewall]') || null;
      this.suggestionsSection = this.querySelector('[data-search-suggestions-section]') 
        || this.querySelectorAll('.wls-search__results-section')?.[0] 
        || null;

      this.collectionsSection = this.querySelector('[data-search-collections-section]') 
        || this.querySelectorAll('.wls-search__results-section')?.[1] 
        || null;

  
      
      this._wireFallbackTargets();
  
      // External trigger
      this.trigger = document.querySelector(this.config.triggerSelector);
  
      if (this.trigger) this.trigger.addEventListener('click', this.handleTriggerClick);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.handleCloseClick);
      if (this.input) this.input.addEventListener('input', this.handleInput);
  
      // Overlay click on host
      this.addEventListener('click', this.handleOverlayClick);
      document.addEventListener('keydown', this.handleKeydown);
  
      // Initial view
      this._setHasResults(false);
    }
  
    disconnectedCallback() {
      if (this.trigger) this.trigger.removeEventListener('click', this.handleTriggerClick);
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.handleCloseClick);
      if (this.input) this.input.removeEventListener('input', this.handleInput);
  
      this.removeEventListener('click', this.handleOverlayClick);
      document.removeEventListener('keydown', this.handleKeydown);
  
      this._abortOngoing();
      clearTimeout(this._debounceTimer);
    }
  
    // ---------- Open / Close ----------
    open() {
      if (this.state.isOpen) return;
      this.state.isOpen = true;
  
      this.classList.add(this.config.activeClass);
      this._lockBody(true);
  
      window.setTimeout(() => this.input?.focus(), 150);
    }
  
    close() {
      if (!this.state.isOpen) return;
      this.state.isOpen = false;
  
      this.classList.remove(this.config.activeClass, this.config.resultsClass);
      this._lockBody(false);
  
      this._abortOngoing();
      this._resetUI();
    }
  
    // ---------- Handlers ----------
    handleTriggerClick(e) {
      e.preventDefault();
      this.open();
    }
  
    handleCloseClick() {
      this.close();
    }
  
    handleOverlayClick(e) {
      if (!this.state.isOpen) return;
      if (!this.inner) return;
  
      // click outside inner closes
      if (!this.inner.contains(e.target)) this.close();
    }
  
    handleKeydown(e) {
      if (e.key !== 'Escape') return;
      if (!this.state.isOpen) return;
      this.close();
    }
  
    handleInput(e) {
      const q = (e.target?.value || '').trim();
      this.state.query = q;
  
      clearTimeout(this._debounceTimer);
  
      if (q.length < this.config.minChars) {
        this._abortOngoing();
        this._setHasResults(false);
        this._clearResults();
        this._updateEmptyText(q);
        return;
      }
  
      this._debounceTimer = window.setTimeout(() => {
        this._fetchPredictive(q);
      }, this.config.debounceMs);
    }
  
    // ---------- Predictive Search ----------
    async _fetchPredictive(query) {
      this._abortOngoing();
      this._abortController = new AbortController();
  
      const params = new URLSearchParams();
      params.set('q', query);
      params.append('resources[type]', 'product,collection,query');

      //params.set('resources[limit]', String(this.config.limit));
  
      try {
        const res = await fetch(`/search/suggest.json?${params.toString()}`, {
          signal: this._abortController.signal,
          headers: { 'Accept': 'application/json' },
        });
  
        if (!res.ok) throw new Error(`Predictive search failed: ${res.status}`);
        const data = await res.json();
  
        const resources = data?.resources?.results || {};
        const products = resources.products || [];
        const collections = resources.collections || [];
        const queries = resources.queries || [];
  
        const hasAny =
          (products && products.length) ||
          (collections && collections.length) ||
          (queries && queries.length);
  
        this._renderAll({ query, products, collections, queries });
        this._setHasResults(Boolean(hasAny));
        this._updateEmptyText(query);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        // fail silently -> show empty state
        this._setHasResults(false);
        this._clearResults();
        this._updateEmptyText(query);
        // console.warn(err);
      }
    }
  
    _abortOngoing() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
    }
  
    // ---------- Rendering ----------
    _renderAll({ query, products, collections, queries }) {
      // suggestions (queries)
      if (this.suggestionsList) {
        this.suggestionsList.innerHTML = (queries || [])
          .map(q => {
            const text = this._escape(q?.text || '');
            const url = this._escape(q?.url || `/search?q=${encodeURIComponent(q?.text || '')}`);
            return `<a class="wls-search__results-item" href="${url}">${text}</a>`;
          })
          .join('');
      }
  
      // collections
      if (this.collectionsList) {
        this.collectionsList.innerHTML = (collections || [])
          .map(c => {
            const title = this._escape(c?.title || '');
            const url = this._escape(c?.url || '#');
            return `<a class="wls-search__results-item" href="${url}">${title}</a>`;
          })
          .join('');
      }
  
      // products
      if (this.productsGrid) {
        this.productsGrid.innerHTML = (products || [])
          .map(p => this._productCard(p))
          .join('');
      }
  
      // View all link
      const viewAllUrl = `/search?q=${encodeURIComponent(query)}`;
      if (this.viewAllLink) this.viewAllLink.setAttribute('href', viewAllUrl);

      const hasSuggestions = (queries || []).length > 0;
      const hasCollections = (collections || []).length > 0;
      const hasProducts = (products || []).length > 0;

      this._setSectionVisible(this.suggestionsSection, hasSuggestions);
      this._setSectionVisible(this.collectionsSection, hasCollections);

      this.productsHeader = this.productsHeader || this.querySelector('.wls-search__results-header');
      this._setSectionVisible(this.productsHeader, hasProducts);

    }
  
    _productCard(p) {
      const title = this._escape(p?.title || '');
      const url = this._escape(p?.url || '#');
  
      // Shopify suggest.json usually returns price in cents-ish depending on setup; handle both.
      const price = this._formatMoney(p?.price);
      const image = p?.image?.url || p?.image || '';
      const img = image
        ? `<img class="wls-search__results-product-image" src="${this._escape(image)}" width="100" height="100" alt="${title}">`
        : '';
  
      return `
        <a class="wls-search__results-product" href="${url}">
          ${img}
          <p class="wls-search__results-product-name">${title}</p>
          <p class="wls-search__results-product-price">${price}</p>
        </a>
      `;
    }
  
    // ---------- UI state ----------
    _setHasResults(has) {
      this.state.hasResults = has;
  
      if (has) {
        this.classList.add(this.config.resultsClass);
        if (this.resultsWrap) this.resultsWrap.style.display = '';
        if (this.emptyWrap) this.emptyWrap.style.display = 'none';
      } else {
        this.classList.remove(this.config.resultsClass);
        if (this.resultsWrap) this.resultsWrap.style.display = 'none';
        if (this.emptyWrap) this.emptyWrap.style.display = '';
      }
    }
  
    _resetUI() {
      if (this.input) this.input.value = '';
      this.state.query = '';
      this._clearResults();
      this._setHasResults(false);
      this._updateEmptyText('');
    }
  
    _clearResults() {
      if (this.suggestionsList) this.suggestionsList.innerHTML = '';
      if (this.collectionsList) this.collectionsList.innerHTML = '';
      if (this.productsGrid) this.productsGrid.innerHTML = '';
    }
  
    _updateEmptyText(query) {
      const textEl = this.emptyWrap?.querySelector('.wls-search__empty-text');
      if (!textEl) return;
  
      if (!query) {
        textEl.textContent = 'Start typing to search…';
        return;
      }
  
      textEl.textContent = `Sorry, nothing found for ${query}`;
    }
  
    _lockBody(lock) {
      // Better than overflow inline style if you later add body lock class in CSS
      if (lock) document.body.classList.add('prevent-scroller');
      else document.body.classList.remove('prevent-scroller');
    }
  
    // ---------- Helpers ----------
    _createEmpty() {
      const div = document.createElement('div');
      div.className = 'wls-search__empty';
      div.innerHTML = `<p class="wls-search__empty-text">Start typing to search…</p>`;
      this.dropdown?.appendChild(div);
      return div;
    }

    _setSectionVisible(sectionEl, isVisible) {
        if (!sectionEl) return;
        sectionEl.style.display = isVisible ? '' : 'none';
    }
      
  
    _wireFallbackTargets() {
      // Your existing markup doesn't have data-* hooks, so find them via structure.
      // Suggestions list = first ".wls-search__results-section" list
      const sections = this.querySelectorAll('.wls-search__results-section');
  
      if (!this.suggestionsList && sections?.[0]) {
        this.suggestionsList = sections[0].querySelector('.wls-search__results-list');
      }
  
      if (!this.collectionsList && sections?.[1]) {
        this.collectionsList = sections[1].querySelector('.wls-search__results-list');
      }
  
      if (!this.productsGrid) {
        this.productsGrid = this.querySelector('.wls-search__results-products');
      }
  
      if (!this.viewAllLink) {
        this.viewAllLink = this.querySelector('.wls-search__results-link');
      }
    }
  
    _escape(str) {
      return String(str)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  
    _formatMoney(value) {
      // Try cents -> money. If value is already string, pass through.
      if (value == null) return '';
      if (typeof value === 'string') return value;
  
      const n = Number(value);
      if (Number.isNaN(n)) return '';
  
      // Heuristic: if it's big, assume cents
      const cents = n >= 1000 ? n : Math.round(n * 100);
  
      // Basic formatting; if you have Shopify.formatMoney available, swap this out.
      const amount = (cents / 100).toFixed(2);
      return `$${amount}`;
    }
  }
  
  if (!customElements.get('wls-search-modal')) {
    customElements.define('wls-search-modal', WlsSearchModal);
  }
  